import React, { FunctionComponent } from 'react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useCards } from 'hooks/useCards';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import { PlaceholderSearchCard } from 'components/PlaceholderSearchCard/PlaceholderSearchCard';
import styles from './MegaMenuSearchList.module.scss';
import { UnorderedListMicrodata } from '../../../List/UnorderedListMicrodata';
import { AnalyticsContextProvider } from 'contexts/AnalyticsContext';
import ImpressionAnalyticsContextProvider from 'contexts/AnalyticsContext/ImpressionAnalyticsContext';
import { SearchNotification } from '../../../Notifications/SearchNotification';
import { Control, List, ListStyle, Service } from '@yleisradio/areena-types';
import { useTranslation } from 'hooks/useTranslation';
import { searchRoutes } from 'utils/routes';
import { useUILanguage } from 'hooks/useUILanguage';
import { TextLinkControl } from 'components/TextLinkControl/TextLinkControl';
import { GridContainer, GridElement } from 'components/Grid';
import MegaMenuSearchListCardPage from './MegaMenuSearchListCardPage';
import useSearchNotification from 'components/Notifications/SearchNotification/useSearchNotification';

const PAGE_SIZE = 6;

type Lists = {
  [key in Service]: List;
};

type Props = {
  searchQuery: string | null;
  lists: Lists;
  service: Service;
  onClick(): void;
};

const PlaceholderCards: FunctionComponent<{
  amount: number;
  listStyle: ListStyle | undefined;
}> = ({ amount, listStyle }) => {
  return Array.from({ length: amount }, (_, i) => (
    <GridElement
      key={i}
      mobile={12}
      tablet={12}
      tabletLandscape={6}
      desktopSmall={12}
    >
      <li className={styles.searchListItemPlaceholder} key={i}>
        <PlaceholderSearchCard listStyle={listStyle} />
      </li>
    </GridElement>
  ));
};

export const MegaMenuSearchList: FunctionComponent<Props> = ({
  lists,
  searchQuery,
  service,
  onClick,
}) => {
  const listContentId = sanitizeHtmlId('megamenu-search-results-content');
  const language = useUILanguage();
  const t = useTranslation();

  const visiblePages = 1;

  const tvCards = useCards({
    source: lists['tv'].source,
    pageIndex: visiblePages - 1,
    pageSize: PAGE_SIZE,
  });

  const radioCards = useCards({
    source: lists['radio'].source,
    pageIndex: visiblePages - 1,
    pageSize: PAGE_SIZE,
  });

  const notification = useSearchNotification(service, {
    tv: tvCards.totalCount,
    radio: radioCards.totalCount,
  });

  const searchPageControl = (): Control => {
    return {
      type: 'navigator',
      icon: { id: 'search', version: '1.0.0' },
      title: t('showAllSearchResults'),
      destination: {
        uri: searchQuery
          ? `/${searchRoutes[language]}?q=${searchQuery}&service=${service}`
          : `/${searchRoutes[language]}?service=${service}`,
      },
    };
  };

  const isLoadingFirstCards = tvCards.loading || radioCards.loading;

  const currentServiceHasResults = () => {
    switch (service) {
      case 'tv':
        return tvCards.totalCount > 0;
      case 'radio':
        return radioCards.totalCount > 0;
      default:
        return false;
    }
  };

  const analyticsContext =
    service === 'tv' ? tvCards.analyticsContext : radioCards.analyticsContext;

  return (
    <div className="VerticalList">
      <AnalyticsContextProvider
        context={
          analyticsContext || lists[service].analytics?.context?.comscore
        }
      >
        <ImpressionAnalyticsContextProvider
          uiElementPosition={0}
          uiElementType="list"
        >
          {!currentServiceHasResults() && !isLoadingFirstCards ? (
            <SearchNotification>{notification}</SearchNotification>
          ) : null}

          <div
            className={styles.wrapper}
            role="group"
            aria-labelledby="searchResults"
          >
            <VisuallyHidden asChild>
              <h2 id="searchResults">{t('searchResults')}</h2>
            </VisuallyHidden>
            <UnorderedListMicrodata className={styles.list} id={listContentId}>
              <GridContainer>
                {isLoadingFirstCards ? (
                  <PlaceholderCards
                    amount={PAGE_SIZE}
                    listStyle={lists[service].style}
                  />
                ) : (
                  <MegaMenuSearchListCardPage
                    listStyle={lists[service].style}
                    pageIndex={0}
                    pageKey={listContentId}
                    pageSize={PAGE_SIZE}
                    cards={service === 'tv' ? tvCards.cards : radioCards.cards}
                    onClick={onClick}
                  />
                )}
              </GridContainer>
            </UnorderedListMicrodata>
          </div>

          {service === 'tv'
            ? tvCards.totalCount > 0 && (
                <div className={styles.searchPageLinkContainer}>
                  <TextLinkControl
                    control={searchPageControl()}
                    onClick={onClick}
                  />
                </div>
              )
            : radioCards.totalCount > 0 && (
                <div className={styles.searchPageLinkContainer}>
                  <TextLinkControl
                    control={searchPageControl()}
                    onClick={onClick}
                  />
                </div>
              )}
        </ImpressionAnalyticsContextProvider>
      </AnalyticsContextProvider>
    </div>
  );
};
